import { type PropsWithChildren, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

type ToggleGroupInputProps<
  T extends string | boolean | null = string | boolean | null,
> = PropsWithChildren<{
  name: string;
  label?: string;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  variant?: "outline" | "default";
  description?: string;
  onChange?: (value: T) => void;
}>;

const ToggleGroupControl = <T extends string | boolean | null>({
  value,
  onChange,
  className,
  error,
  variant,
  children,
}: {
  value: T;
  onChange: (value: T) => void;
  className?: string;
  error?: boolean;
  variant?: "outline" | "default";
  children: React.ReactNode;
}) => {
  const stringValue =
    value === true ? "true" : value === false ? "false" : (value as string);

  const handleChange = (_value: string) => {
    let value: string | boolean | null = _value;
    if (_value === "true") value = true;
    if (_value === "false") value = false;
    if (_value === "") value = null;
    onChange(value as T);
  };

  return (
    <ToggleGroup
      type="single"
      className={className}
      value={stringValue}
      variant={variant}
      onValueChange={handleChange}
      aria-invalid={error ? true : undefined}
    >
      {children}
    </ToggleGroup>
  );
};

export const ToggleGroupInput = <
  T extends string | boolean | null = string | boolean | null,
>({
  name,
  label,
  className,
  description,
  onChange,
  variant,
  children,
}: ToggleGroupInputProps<T>) => {
  const form = useFormContext();
  const { error } = useFormField();

  return (
    <FormItem>
      {label && <FormLabel>{label}</FormLabel>}
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <ToggleGroupControl
            value={field.value}
            onChange={(value) => {
              onChange?.(value);
              field.onChange(value);
            }}
            className={className}
            variant={variant}
            error={!!error}
          >
            {children}
          </ToggleGroupControl>
        )}
      />
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
};

type ToggleGroupInputItemProps = PropsWithChildren<{
  value: string | boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  variant?: "outline" | "default";
}>;

export const ToggleGroupInputItem = ({
  value,
  className,
  variant,
  children,
}: ToggleGroupInputItemProps) => {
  const stringValue = typeof value === "boolean" ? String(value) : value;

  return (
    <ToggleGroupItem
      value={stringValue}
      variant={variant}
      className={className}
    >
      {children}
    </ToggleGroupItem>
  );
};
